import React from 'react'
import { Theme } from '@material-ui/core'
import { DeliverableValueStatus } from '../../../../../models'
import { Close, ChevronLeft, ChevronRight } from '@material-ui/icons'

export interface SummaryData {
    total: number
    matched: number
    notMatched: number
    missingRentRoll: number
    missingDocument: number
    matchFixed: number
    accepted: number
}

interface SummarySectionProps {
    theme: Theme
    summary: SummaryData
    totalRows: number
    statusFilter: DeliverableValueStatus | 'ALL'
    setStatusFilter: (status: DeliverableValueStatus | 'ALL') => void
    columnLabel: string
    onClose: () => void
    onPreviousColumn?: () => void
    onNextColumn?: () => void
    hasPreviousColumn?: boolean
    hasNextColumn?: boolean
}

// Helper function to get percentage
const getPercentage = (value: number, total: number) => {
    if (total === 0) return '0%'
    return `${Math.round((value / total) * 100)}%`
}

export const SummarySection: React.FC<SummarySectionProps> = ({
    theme,
    summary,
    totalRows,
    statusFilter,
    setStatusFilter,
    columnLabel,
    onClose,
    onPreviousColumn,
    onNextColumn,
    hasPreviousColumn = false,
    hasNextColumn = false,
}) => {
    // Check if navigation is possible based on passed props and handlers
    const canNavigatePrevious = Boolean(onPreviousColumn) && hasPreviousColumn
    const canNavigateNext = Boolean(onNextColumn) && hasNextColumn

    return (
        <div
            style={{
                padding: '16px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.grey[50],
            }}
        >
            {/* Title section with close button */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                        onClick={onPreviousColumn}
                        disabled={!canNavigatePrevious}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: canNavigatePrevious ? 'pointer' : 'default',
                            opacity: canNavigatePrevious ? 1 : 0.5,
                            marginRight: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '4px',
                        }}
                        title="Previous column"
                    >
                        <ChevronLeft />
                    </button>
                    <h3 style={{ margin: 0 }}>{columnLabel} Column Audit</h3>
                    <button
                        onClick={onNextColumn}
                        disabled={!canNavigateNext}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: canNavigateNext ? 'pointer' : 'default',
                            opacity: canNavigateNext ? 1 : 0.5,
                            marginLeft: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '4px',
                        }}
                        title="Next column"
                    >
                        <ChevronRight />
                    </button>
                </div>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Close />
                </button>
            </div>

            <div
                style={{
                    fontStyle: 'italic',
                    fontSize: '14px',
                    marginBottom: '12px',
                    color: theme.palette.text.secondary,
                }}
            >
                Showing all {summary.total} values for this column across the
                entire dataset
                {totalRows > 500 && ` (${totalRows} total records)`}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ minWidth: '120px' }}>
                    <div style={{ fontWeight: 'bold' }}>Total Values</div>
                    <div>{summary.total}</div>
                </div>
                <div style={{ minWidth: '120px' }}>
                    <div
                        style={{
                            fontWeight: 'bold',
                            color: theme.palette.success.main,
                        }}
                    >
                        Matched
                    </div>
                    <div>
                        {summary.matched} (
                        {getPercentage(summary.matched, summary.total)})
                    </div>
                </div>
                <div style={{ minWidth: '120px' }}>
                    <div
                        style={{
                            fontWeight: 'bold',
                            color: theme.palette.success.main,
                        }}
                    >
                        Fixed
                    </div>
                    <div>
                        {summary.matchFixed} (
                        {getPercentage(summary.matchFixed, summary.total)})
                    </div>
                </div>
                <div style={{ minWidth: '120px' }}>
                    <div
                        style={{
                            fontWeight: 'bold',
                            color: theme.palette.error.main,
                        }}
                    >
                        Not Matched
                    </div>
                    <div>
                        {summary.notMatched} (
                        {getPercentage(summary.notMatched, summary.total)})
                    </div>
                </div>
                <div style={{ minWidth: '120px' }}>
                    <div
                        style={{
                            fontWeight: 'bold',
                            color: theme.palette.warning.main,
                        }}
                    >
                        Missing Data
                    </div>
                    <div>
                        {summary.missingRentRoll + summary.missingDocument} (
                        {getPercentage(
                            summary.missingRentRoll + summary.missingDocument,
                            summary.total,
                        )}
                        )
                    </div>
                </div>
                <div style={{ minWidth: '120px' }}>
                    <div
                        style={{
                            fontWeight: 'bold',
                            color: theme.palette.success.dark,
                        }}
                    >
                        Accepted
                    </div>
                    <div>
                        {summary.accepted} (
                        {getPercentage(summary.accepted, summary.total)})
                    </div>
                </div>
            </div>

            {/* Filter controls */}
            <div style={{ marginTop: '16px' }}>
                <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
                    Filter by Status
                </div>
                <div
                    style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                    }}
                >
                    <button
                        onClick={() => setStatusFilter('ALL')}
                        style={{
                            padding: '6px 12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            backgroundColor:
                                statusFilter === 'ALL'
                                    ? theme.palette.primary.main
                                    : 'transparent',
                            color:
                                statusFilter === 'ALL'
                                    ? 'white'
                                    : theme.palette.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setStatusFilter('MATCHED')}
                        style={{
                            padding: '6px 12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            backgroundColor:
                                statusFilter === 'MATCHED'
                                    ? theme.palette.success.main
                                    : 'transparent',
                            color:
                                statusFilter === 'MATCHED'
                                    ? 'white'
                                    : theme.palette.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        Matched
                    </button>
                    <button
                        onClick={() => setStatusFilter('MATCH_FIXED')}
                        style={{
                            padding: '6px 12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            backgroundColor:
                                statusFilter === 'MATCH_FIXED'
                                    ? theme.palette.success.main
                                    : 'transparent',
                            color:
                                statusFilter === 'MATCH_FIXED'
                                    ? 'white'
                                    : theme.palette.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        Fixed
                    </button>
                    <button
                        onClick={() => setStatusFilter('ACCEPTED')}
                        style={{
                            padding: '6px 12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            backgroundColor:
                                statusFilter === 'ACCEPTED'
                                    ? theme.palette.success.dark
                                    : 'transparent',
                            color:
                                statusFilter === 'ACCEPTED'
                                    ? 'white'
                                    : theme.palette.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        Accepted
                    </button>
                    <button
                        onClick={() => setStatusFilter('NOT_MATCHED')}
                        style={{
                            padding: '6px 12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            backgroundColor:
                                statusFilter === 'NOT_MATCHED'
                                    ? theme.palette.error.main
                                    : 'transparent',
                            color:
                                statusFilter === 'NOT_MATCHED'
                                    ? 'white'
                                    : theme.palette.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        Not Matched
                    </button>
                    <button
                        onClick={() => setStatusFilter('MISSING_RENT_ROLL')}
                        style={{
                            padding: '6px 12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            backgroundColor:
                                statusFilter === 'MISSING_RENT_ROLL'
                                    ? theme.palette.warning.main
                                    : 'transparent',
                            color:
                                statusFilter === 'MISSING_RENT_ROLL'
                                    ? 'white'
                                    : theme.palette.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        Missing Rent Roll
                    </button>
                    <button
                        onClick={() => setStatusFilter('MISSING_DOCUMENT')}
                        style={{
                            padding: '6px 12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            backgroundColor:
                                statusFilter === 'MISSING_DOCUMENT'
                                    ? theme.palette.warning.main
                                    : 'transparent',
                            color:
                                statusFilter === 'MISSING_DOCUMENT'
                                    ? 'white'
                                    : theme.palette.text.primary,
                            cursor: 'pointer',
                        }}
                    >
                        Missing Document
                    </button>
                </div>
            </div>
        </div>
    )
}
